import {
  Component,
  EventEmitter,
  HostListener,
  Input,
  Output,
  TemplateRef,
  ViewChild,
  OnDestroy,
  computed,
  inject,
} from "@angular/core";
import { UntypedFormBuilder } from "@angular/forms";
import { MatDialog, MatDialogRef } from "@angular/material/dialog";
import { ActivatedRoute, Router } from "@angular/router";
import { Subject } from "rxjs";
import { takeUntil } from "rxjs/operators";

import { RegisterComponent as BaseRegisterComponent } from "../../../../../../libs/angular/src/components/register.component";
import { ApiService } from "@bitwarden/common/abstractions/api.service";
import { AuthService } from "@bitwarden/common/abstractions/auth.service";
import { CryptoService } from "@bitwarden/common/abstractions/crypto.service";
import { EnvironmentService } from "@bitwarden/common/abstractions/environment.service";
import { FormValidationErrorsService } from "@bitwarden/common/abstractions/formValidationErrors.service";
import { I18nService } from "@bitwarden/common/abstractions/i18n.service";
import { LogService } from "@bitwarden/common/abstractions/log.service";
import { PasswordGenerationService } from "@bitwarden/common/abstractions/passwordGeneration.service";
import { PlatformUtilsService } from "@bitwarden/common/abstractions/platformUtils.service";
import { PolicyService } from "@bitwarden/common/abstractions/policy/policy.service.abstraction";
import { StateService } from "@bitwarden/common/abstractions/state.service";
import { MasterPasswordPolicyOptions } from "@bitwarden/common/models/domain/master-password-policy-options";
import { ReferenceEventRequest } from "@bitwarden/common/models/request/reference-event.request";
import { StripeConfigService } from "../../services/api/stripe-config.service";
import { HelperTooltip } from "../../shared/utils/helper-tooltip";

import { ConfirmationDialogService } from "../../services/confirmation/confirmation.service";
import { DeviceDetector, DeviceDetection } from "../../shared/utils/device-detector";
import { ReferralApi } from "../../services/DataService/referral/referral.api";

class templateRef {}

@Component({
  selector: "app-register-form",
  templateUrl: "./register-form.component.html",
  providers: [StripeConfigService],
})
export class RegisterFormComponent extends BaseRegisterComponent implements OnDestroy {
  @Input() queryParamEmail: string;
  @Input() enforcedPolicyOptions: MasterPasswordPolicyOptions;
  @Input() referenceDataValue: ReferenceEventRequest;

  @ViewChild("notificationPassword") notificationPassword: TemplateRef<any>;
  @Output() submitNotification: EventEmitter<templateRef> = new EventEmitter();

  private deviceDetector: DeviceDetection;
  private unsubscribe$ = new Subject<void>();
  private sessionId: string;
  checkboxclicked = false;
  showErrorSummary = false;
  isStripeLoaded = false;
  isLoading = false;
  version: string;
  tooltip: HelperTooltip;

  // The popover's only other dismiss affordances are the close button and the backdrop click —
  // neither is reachable by Escape without this, since the popover is plain markup (not a CDK
  // overlay) with no built-in Escape handling of its own.
  @HostListener("document:keydown.escape")
  onEscapeKey(): void {
    if (this.tooltip?.showHelpTooltip) {
      this.tooltip.close();
    }
  }

  year = "2015";
  referralCodeStatus: "idle" | "checking" | "valid" | "invalid" | "error" = "idle";
  showHintInput = false;
  showReferralInput = false;

  private referralApi = inject(ReferralApi);
  private _validationPromise: Promise<void> | null = null;
  private _validationSeq = 0;
  private _validatedReferralCode: string | null = null;
  private _isSubmitting = false;

  constructor(
    private policyService: PolicyService,
    private route: ActivatedRoute,
    private stripeConfigService: StripeConfigService,
    formValidationErrorService: FormValidationErrorsService,
    formBuilder: UntypedFormBuilder,
    authService: AuthService,
    router: Router,
    i18nService: I18nService,
    cryptoService: CryptoService,
    apiService: ApiService,
    stateService: StateService,
    platformUtilsService: PlatformUtilsService,
    passwordGenerationService: PasswordGenerationService,
    environmentService: EnvironmentService,
    logService: LogService,
    dialog: MatDialog,
    confirmationDialogService: ConfirmationDialogService,
  ) {
    super(
      formValidationErrorService,
      formBuilder,
      authService,
      router,
      i18nService,
      cryptoService,
      apiService,
      stateService,
      platformUtilsService,
      passwordGenerationService,
      environmentService,
      logService,
      dialog,
      confirmationDialogService,
    );
    this.tooltip = new HelperTooltip();

    this.deviceDetector = new DeviceDetector();
  }

  async ngOnInit() {
    this.year = new Date().getFullYear().toString();
    this.version = await this.platformUtilsService.getApplicationVersion();
    await super.ngOnInit();
    await this.initializeForm();
    this.referenceData = this.referenceDataValue;
    this.submitNotification.emit(this.notificationPassword);
  }

  ngOnDestroy() {
    this.unsubscribe$.next();
    this.unsubscribe$.complete();
  }

  hideHintInput() {
    this.showHintInput = false;
    this.formGroup.get("hint")?.setValue("");
  }

  hideReferralInput() {
    this.showReferralInput = false;
    this.formGroup.get("referralCode")?.setValue("");
    this.onReferralCodeInput();
  }

  onCheckboxChange(value: boolean) {
    this.formGroup.get("passwordDeclaration").setValue(value);
  }

  get isCheckboxInvalid(): boolean {
    const isChecked = this.formGroup.get("passwordDeclaration").value;
    return !isChecked && this.showErrorSummary;
  }

  onReferralCodeInput(): void {
    this._validationSeq++;
    this._validationPromise = null;
    this._validatedReferralCode = null;
    this.referralCodeStatus = "idle";
  }

  onReferralCodeBlur(): void {
    const code = this.formGroup.value.referralCode?.trim() ?? "";
    if (!code) {
      this.referralCodeStatus = "idle";
      this._validatedReferralCode = null;
      return;
    }
    this.referralCodeStatus = "checking";
    this._validationPromise = this._validateReferralCode(code);
  }

  private async _validateReferralCode(code: string): Promise<void> {
    // Bump per run so a slow validation of a now-edited code can't overwrite the
    // status of a newer run (user edits + re-blurs while the first is in flight).
    const seq = ++this._validationSeq;
    try {
      // Both start immediately; we wait at least 500ms regardless of API speed
      // to prevent timing attacks that probe referral code length.
      const validPromise = this.referralApi.validateReferralCode(code);
      await new Promise<void>((resolve) => setTimeout(resolve, 500));
      const valid = await validPromise;
      if (seq !== this._validationSeq) {
        return;
      }
      this._validatedReferralCode = code;
      this.referralCodeStatus = valid ? "valid" : "invalid";
    } catch {
      if (seq !== this._validationSeq) {
        return;
      }
      this._validatedReferralCode = code;
      // API unreachable — block submission; user must contact support.
      this.referralCodeStatus = "error";
    }
  }

  private async ensureReferralCodeValidForSubmit(referralCode: string): Promise<boolean> {
    if (this.referralCodeStatus === "checking" && this._validationPromise) {
      await this._validationPromise;
    }

    if (
      this._validatedReferralCode !== referralCode ||
      (this.referralCodeStatus !== "valid" &&
        this.referralCodeStatus !== "invalid" &&
        this.referralCodeStatus !== "error")
    ) {
      this.referralCodeStatus = "checking";
      this._validationPromise = this._validateReferralCode(referralCode);
      await this._validationPromise;
    }

    if (this._validatedReferralCode !== referralCode || this.referralCodeStatus === "checking") {
      return false;
    }

    if (this.referralCodeStatus === "invalid") {
      this.platformUtilsService.showToast(
        "error",
        this.i18nService.t("invalidReferralCodeTitle"),
        this.i18nService.t("invalidReferralCodeDesc"),
      );
      return false;
    }

    if (this.referralCodeStatus === "error") {
      this.platformUtilsService.showToast(
        "error",
        this.i18nService.t("referralCodeApiErrorTitle"),
        this.i18nService.t("referralCodeApiErrorDesc"),
      );
      return false;
    }

    return this.referralCodeStatus === "valid";
  }

  // isLoading only flips after referral validation, the confirmation dialog and the policy
  // check have all awaited; _isSubmitting covers that window from the first click.
  protected get isBusy(): boolean {
    return this.isLoading || this._isSubmitting;
  }

  async submit(): Promise<boolean> {
    if (this.isBusy) {
      return false;
    }

    this._isSubmitting = true;

    try {
      const referralCode = this.formGroup.value.referralCode?.trim() ?? "";

      if (referralCode) {
        const isReferralCodeValid = await this.ensureReferralCodeValidForSubmit(referralCode);
        if (!isReferralCodeValid) {
          return false;
        }
      }

      this.showErrorSummary = true;
      if (!this.formGroup.invalid) {
        const userConfirmed = await this.notificationDialogOpen();
        if (!userConfirmed) {
          return false;
        }
      }
      if (
        this.enforcedPolicyOptions != null &&
        !this.policyService.evaluateMasterPassword(
          this.passwordStrengthResult.score,
          this.formGroup.value.masterPassword,
          this.enforcedPolicyOptions,
        )
      ) {
        this.platformUtilsService.showToast(
          "error",
          this.i18nService.t("errorOccurred"),
          this.i18nService.t("masterPasswordPolicyRequirementsNotMet"),
        );
        return false;
      }

      this.isLoading = true;
      // Yield to the event loop so the browser can repaint before PBKDF2 blocks the main thread.
      await new Promise<void>((resolve) => setTimeout(resolve, 0));

      const wasAccountCreated = this.accountCreated;
      const registrationSucceeded = await super.submit(false);
      if (referralCode && !wasAccountCreated && this.accountCreated) {
        this.referralApi.storePendingReferralCode(referralCode, this.formGroup.value.email);
      }
      return registrationSucceeded;
    } finally {
      this._isSubmitting = false;
      this.isLoading = false;
    }
  }
  notificationRef: MatDialogRef<any>;

  protected isPassWordInlineInvalid = computed(() => {
    return (
      this.formGroup.get("passwordDeclaration").errors &&
      this.formGroup.invalid &&
      this.formGroup.get("passwordDeclaration").touched
    );
  });

  openDialog(templateRef: TemplateRef<any>) {
    // Same panelClass family as ConfirmationDialogComponent's warning/info dialogs (e.g. the
    // weak-password confirmation) — not the full-page mobile dialog classes, which force
    // height/width to 100% under 768px (material-ui.scss), stretching this small popup across
    // the whole screen. "confirmation-dialog" (width: 500px) isn't included: it's inert here —
    // %confirmation-dialog--glass-surface's width: 400px wins by source order regardless.
    this.notificationRef = this.dialog.open(templateRef, {
      panelClass: ["confirmation-dialog--warning"],
      disableClose: true,
      // Bounds the pane so the surface (max-height: inherit) scrolls instead of overflowing
      // off-screen — with disableClose there is no other way to dismiss this dialog.
      maxHeight: "90vh",
    });
  }

  notificationDialogOpen(): Promise<boolean> {
    return new Promise((resolve) => {
      this.openDialog(this.notificationPassword);
      this.notificationRef
        .afterClosed()
        .pipe(takeUntil(this.unsubscribe$))
        .subscribe((result) => {
          if (result === "userConfirmed") {
            resolve(true);
          } else {
            resolve(false);
          }
        });
    });
  }

  submitStart() {
    this.notificationRef.close("userConfirmed");
  }

  resetPassword() {
    this.notificationRef.close("userCancelled");
  }

  private async initializeForm() {
    this.isStripeLoaded = true;
    return;
    // todo removed the following code because of apple request in-store app purchase.
    // We will reimpllement stripe and the paywall in the future.
    // try {
    //   this.getQueryParams();
    //
    //   if (!this.sessionId) {
    //     if (this.environmentService.isLocal() || this.deviceDetector.isIphoneDevice()) {
    //       this.isStripeLoaded = true;
    //     }
    //
    //   }
    //
    //
    //
    //   const checkoutDetail = await this.getCheckoutDetail();
    //   if (checkoutDetail) {
    //     this.setCheckoutFormDetail(checkoutDetail);
    //     this.isStripeLoaded = true;
    //   }
    // } catch (error) {
    //   // todo: display error on page
    // } finally {
    //   if (this.environmentService.isLocal() || this.deviceDetector.isIphoneDevice()) {
    //     this.isStripeLoaded = true;
    //   }
    // }
  }

  private getQueryParams() {
    this.route.queryParams.pipe(takeUntil(this.unsubscribe$)).subscribe((params) => {
      this.sessionId = params["session"];
    });
  }

  private async getCheckoutDetail() {
    return await this.stripeConfigService.getCheckoutSessions(this.sessionId);
  }

  private setCheckoutFormDetail(checkoutDetail: Record<string, any>) {
    this.formGroup.get("email").setValue(checkoutDetail.email);
    this.formGroup.get("email").disable();

    this.formGroup.get("name").setValue(checkoutDetail.name);
    this.formGroup.get("name").disable();
  }
}
